import {
  HttpClient,
  HttpEvent,
  HttpEventType,
  HttpResponse
} from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import { sum, toPairs, values } from "lodash-es";
import {
  defer,
  forkJoin,
  iif,
  Observable,
  Subject,
  of,
  lastValueFrom
} from "rxjs";
import { filter, map, switchMap, tap } from "rxjs/operators";
import { delayedRetry } from "src/app/support/rxjs";
import { environment } from "src/environments/environment";
import { S3Client, PutObjectCommand } from "@aws-sdk/client-s3";
import { Upload } from "@aws-sdk/lib-storage";
import { omitUndefinedAndNullProperties } from "src/app/support/object";
import {
  UploadProgressTracker,
  UploadProgressInfo
} from "src/app/support/upload-progress";

const CHUNK_SIZE = 1024 * 1024 * 5;

type UploadStateSnapshot = {
  total: number;
  loaded: number;
  id: string;
  done: boolean;
};

type CreateUploadResponsePayload = {
  name: string;
  parts: number;
  public?: boolean;
};

type CreateUploadResponse = {
  id: string;
  urls: Record<number, string>;
  uploadId?: string;
};

type CreateUploadResponseV2 = {
  id: string;
  bucket: string;
  key: string;
  accessKeyId: string;
  secretAccessKey: string;
  sessionToken: string;
  expiration: string;
  region: string;
};

@Injectable()
export class StorageBackend {
  private readonly http = inject(HttpClient);

  async uploadV2({
    name,
    body,
    storageType,
    abortController,
    sharings,
    onProgress
  }: {
    name: string;
    body: Blob;
    abortController?: AbortController;
    sharings?: Array<Record<string, string>>;
    storageType?: "temporary" | "default" | "public" | "archive";
    onProgress?: (progress: UploadProgressInfo) => void;
  }): Promise<{ storageId: string }> {
    const response = await lastValueFrom(
      this.http.post<CreateUploadResponseV2>(
        `${environment.snoots.api.storage}/uploads`,
        omitUndefinedAndNullProperties({
          name,
          storageType,
          sharings
        })
      )
    );

    // Inizializza il tracker per progress più fluido
    const progressTracker = new UploadProgressTracker(body.size, onProgress);
    progressTracker.start();

    const parallelUploads3 = new Upload({
      client: new S3Client({
        // ToDo: Remove workaround once https://github.com/aws/aws-sdk-js-v3/issues/6834 is fixed.
        requestChecksumCalculation: "WHEN_REQUIRED",
        region: response.region,
        credentials: {
          accessKeyId: response.accessKeyId,
          secretAccessKey: response.secretAccessKey,
          sessionToken: response.sessionToken
        }
      }),
      params: {
        Bucket: response.bucket,
        Key: response.key,
        Body: body.stream()
      },
      tags: [],
      queueSize: 4,
      partSize: 1024 * 1024 * 5, // 1MB invece di 5MB per progress più frequente
      leavePartsOnError: false,
      abortController
    });

    parallelUploads3.on("httpUploadProgress", (progress) => {
      progressTracker.updateProgress(progress.loaded || 0);
    });

    try {
      await parallelUploads3.done();
      progressTracker.complete();
    } catch (error) {
      progressTracker.stop();
      throw error;
    }

    return {
      storageId: response.id
    };
  }

  async uploadV3({
    name,
    body,
    storageType,
    temporary
  }: {
    name: string;
    body: Blob;
    storageType?: "temporary" | "default" | "public" | "archive";
    temporary?: boolean;
  }): Promise<{ storageId: string }> {
    const response = await lastValueFrom(
      this.http.post<CreateUploadResponseV2>(
        `${environment.snoots.api.storage}/uploads`,
        omitUndefinedAndNullProperties({
          name,
          storageType,
          temporary
        })
      )
    );

    const command = new PutObjectCommand({
      Bucket: response.bucket,
      Key: response.key,
      Body: body
    });

    const client = new S3Client({
      // ToDo: Remove workaround once https://github.com/aws/aws-sdk-js-v3/issues/6834 is fixed.
      requestChecksumCalculation: "WHEN_REQUIRED",
      credentials: {
        accessKeyId: response.accessKeyId,
        secretAccessKey: response.secretAccessKey,
        sessionToken: response.sessionToken
      },
      region: response.region
    });

    await client.send(command);

    return { storageId: response.id };
  }

  uploadPublic(
    file: File,
    linkToEntityFactory$: ((storageId: string) => Observable<any>) | null = null
  ) {
    return this.upload(file, linkToEntityFactory$, true);
  }

  upload(
    file: File,
    linkToEntityFactory$:
      | ((storageId: string) => Observable<any>)
      | null = null,
    isPublic: boolean = false
  ): Observable<UploadStateSnapshot> {
    return this.uploadUploadResponse(
      file,
      file.name,
      file.size,
      isPublic,
      (payload) => {
        return this.http.post<CreateUploadResponse>(
          `${environment.snoots.api.storage}/uploads`,
          payload
        );
      },
      linkToEntityFactory$
    );
  }

  update(
    fileId: string,
    fileName: string,
    blob: Blob
  ): Observable<UploadStateSnapshot> {
    return this.uploadUploadResponse(
      blob,
      fileName,
      blob.size,
      false,
      (payload) => {
        return this.http.put<CreateUploadResponse>(
          `${environment.snoots.api.storage}/files/${fileId}/upload`,
          payload
        );
      },
      null
    );
  }

  private uploadUploadResponse(
    file: Blob,
    fileName: string,
    fileSize: number,
    isPublic: boolean,
    createUploadResponseFn: (
      payload: CreateUploadResponsePayload
    ) => Observable<CreateUploadResponse>,
    linkToEntityFactory$: ((storageId: string) => Observable<any>) | null = null
  ): Observable<UploadStateSnapshot> {
    const subject = new Subject<UploadStateSnapshot>();
    const loadedMap: Record<string, number> = {};

    const payload: CreateUploadResponsePayload = {
      name: fileName,
      parts: Math.ceil(fileSize / CHUNK_SIZE)
    };

    if (isPublic) {
      payload.public = true;
    }

    const createUploadResponse$ = createUploadResponseFn(payload);

    createUploadResponse$
      .pipe(
        switchMap((uploadResponse) =>
          iif(
            () => !!linkToEntityFactory$,
            defer(() =>
              linkToEntityFactory$!(uploadResponse.id).pipe(
                map(() => uploadResponse)
              )
            ),
            of(uploadResponse)
          )
        ),
        switchMap((uploadResponse) => {
          return forkJoin(
            toPairs(uploadResponse.urls).map(([chunkNumber, url], index) =>
              this.http
                .put(
                  `noauth::${url}`,
                  file.slice(index * CHUNK_SIZE, ++index * CHUNK_SIZE),
                  {
                    observe: "events",
                    reportProgress: true
                  }
                )
                .pipe(
                  tap((httpEvent) => {
                    switch (httpEvent.type) {
                      case HttpEventType.UploadProgress: {
                        subject.next({
                          done: false,
                          id: uploadResponse.id,
                          total: file.size,
                          loaded: updateAndGetLoadedByMap(
                            loadedMap,
                            chunkNumber,
                            httpEvent.loaded
                          )
                        });
                        break;
                      }
                      case HttpEventType.Response: {
                        subject.next({
                          done: true,
                          id: uploadResponse.id,
                          total: file.size,
                          loaded: file.size
                        });
                        break;
                      }
                    }
                  }),
                  delayedRetry(1000),
                  filter(
                    (httpEvent) => httpEvent.type === HttpEventType.Response
                  ),
                  map((uploadPartResponse: HttpEvent<any>) => ({
                    etag: getETag(uploadPartResponse),
                    number: parseInt(chunkNumber)
                  }))
                )
            )
          ).pipe(
            switchMap((multipartUploadResults) => {
              return this.http.put(
                `${environment.snoots.api.storage}/uploads/${uploadResponse.id}/complete`,
                { parts: multipartUploadResults }
              );
            }),
            map(() => subject.complete())
          );
        })
      )
      .toPromise()
      .catch((ex) => subject.error(ex));

    return subject;
  }

  download(
    id: string,
    contentDisposition: "inline" | "attachment" = "attachment"
  ): Observable<string> {
    return this.http
      .get<{ url: string }>(
        `${environment.snoots.api.storage}/files/${id}/download-url`,
        {
          params: {
            "content-disposition": contentDisposition
          }
        }
      )
      .pipe(
        map((response) => {
          return response.url;
        })
      );
  }

  downloadForLanding(
    id: string,
    token: string,
    contentDisposition: "inline" | "attachment" = "attachment"
  ): Observable<string> {
    return this.http
      .get<{ url: string }>(
        `${environment.snoots.api.storage}/files/${id}/download-url`,
        {
          params: {
            "content-disposition": contentDisposition
          },
          headers: {
            member: `Bearer ${token}`
          }
        }
      )
      .pipe(
        map((response) => {
          return response.url;
        })
      );
  }

  delete(id: string): Observable<void> {
    return this.http.delete<void>(
      `${environment.snoots.api.storage}/files/${id}`
    );
  }
}

function getETag(uploadPartResponse: HttpEvent<any>): string | undefined {
  return (<HttpResponse<any>>uploadPartResponse).headers
    .get("ETag")
    ?.replace(/"/gi, "");
}

function updateAndGetLoadedByMap(
  loadedMap: Record<string, number>,
  chunkNumber: string,
  loaded: number
): number {
  loadedMap[chunkNumber] = loaded;
  return sum(values(loadedMap));
}
